import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================
// ContactModal — Finestra contatti bilingue IT/EN
// Usage: <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
// Aggiungi in it.json / en.json le chiavi indicate in fondo al file
// ============================================================

export default function ContactModal({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';

  // Blocca lo scroll del body quando il modal è aperto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Chiude cliccando fuori dall'overlay
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(10, 14, 26, 0.72)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'cm-fadein 0.22s ease',
      }}
    >
      <style>{`
        @keyframes cm-fadein  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cm-slidein { from { opacity: 0; transform: translateY(28px) scale(0.97); }
                                to   { opacity: 1; transform: translateY(0)     scale(1);    } }
        .cm-card {
          background: #ffffff;
          border-radius: 20px;
          max-width: 440px;
          width: 100%;
          box-shadow: 0 32px 80px rgba(80, 60, 180, 0.22), 0 8px 24px rgba(0,0,0,0.14);
          overflow: hidden;
          animation: cm-slidein 0.28s cubic-bezier(0.34,1.56,0.64,1);
          position: relative;
        }
        .cm-header {
          background: linear-gradient(135deg, #0d1b3e 0%, #1a2a5e 50%, #2d1b69 100%);
          padding: 36px 32px 28px;
          text-align: center;
          position: relative;
        }
        .cm-close {
          position: absolute;
          top: 14px;
          right: 16px;
          background: rgba(255,255,255,0.12);
          border: none;
          color: #fff;
          width: 32px; height: 32px;
          border-radius: 50%;
          font-size: 18px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.18s;
          line-height: 1;
        }
        .cm-close:hover { background: rgba(255,255,255,0.25); }
        .cm-logo {
          width: 140px;
          height: auto;
          margin-bottom: 4px;
          filter: drop-shadow(0 2px 12px rgba(120,80,255,0.35));
        }
        .cm-tagline {
          color: rgba(160,185,255,0.85);
          font-size: 12px;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-family: 'Segoe UI', system-ui, sans-serif;
          margin: 0;
        }
        .cm-body {
          padding: 28px 32px 32px;
        }
        .cm-section-title {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #8860ff;
          margin: 0 0 14px;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }
        .cm-desc {
          font-size: 14.5px;
          color: #444;
          line-height: 1.65;
          margin: 0 0 22px;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }
        .cm-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, #e0d8ff, transparent);
          margin: 20px 0;
        }
        .cm-email-block {
          display: flex;
          align-items: center;
          gap: 14px;
          background: linear-gradient(135deg, #f5f0ff, #eef4ff);
          border-radius: 12px;
          padding: 16px 18px;
          border: 1px solid #ddd5ff;
          margin-bottom: 20px;
        }
        .cm-email-icon {
          width: 40px; height: 40px;
          background: linear-gradient(135deg, #7c4dff, #2979ff);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(100,60,255,0.3);
        }
        .cm-email-label {
          font-size: 11px;
          color: #888;
          font-family: 'Segoe UI', system-ui, sans-serif;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
        }
        .cm-email-link {
          font-size: 15px;
          font-weight: 600;
          color: #4a2daa;
          text-decoration: none;
          font-family: 'Segoe UI', system-ui, sans-serif;
          letter-spacing: 0.2px;
          display: block;
          transition: color 0.15s;
        }
        .cm-email-link:hover { color: #7c4dff; }
        .cm-btn-mail {
          display: block;
          width: 100%;
          padding: 13px;
          background: linear-gradient(135deg, #7c4dff 0%, #2979ff 100%);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 14.5px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Segoe UI', system-ui, sans-serif;
          letter-spacing: 0.3px;
          transition: opacity 0.18s, transform 0.18s;
          box-shadow: 0 6px 20px rgba(100,60,255,0.35);
          text-decoration: none;
          text-align: center;
        }
        .cm-btn-mail:hover { opacity: 0.9; transform: translateY(-1px); }
        .cm-footer {
          text-align: center;
          font-size: 11.5px;
          color: #aaa;
          margin-top: 18px;
          font-family: 'Segoe UI', system-ui, sans-serif;
          line-height: 1.5;
        }
        .cm-footer a { color: #8860ff; text-decoration: none; }
      `}</style>

      <div className="cm-card">
        {/* ── HEADER con logo ── */}
        <div className="cm-header">
          <button className="cm-close" onClick={onClose} aria-label="Chiudi">✕</button>
          <img
            src="/AppImages/logo.png"
            alt="Vector Minds"
            className="cm-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="cm-tagline">AI Hiking · Vector Minds</p>
        </div>

        {/* ── BODY ── */}
        <div className="cm-body">
          <p className="cm-section-title">
            {lang === 'it' ? 'Scrivici' : 'Get in touch'}
          </p>

          <p className="cm-desc">
            {lang === 'it'
              ? 'Hai domande, suggerimenti o vuoi collaborare con noi? Il team di Vector Minds è a tua disposizione. Rispondiamo di solito entro 24–48 ore.'
              : 'Have questions, suggestions or want to collaborate? The Vector Minds team is here for you. We usually reply within 24–48 hours.'}
          </p>

          {/* Email card */}
          <div className="cm-email-block">
            <div className="cm-email-icon">✉️</div>
            <div>
              <div className="cm-email-label">
                {lang === 'it' ? 'Indirizzo email' : 'Email address'}
              </div>
              <a className="cm-email-link" href="mailto:info@aihiking.eu">
                info@aihiking.eu
              </a>
            </div>
          </div>

          {/* CTA button */}
          <a
            className="cm-btn-mail"
            href="mailto:info@aihiking.eu"
          >
            {lang === 'it' ? '✉️ Invia una email' : '✉️ Send us an email'}
          </a>

          <div className="cm-divider" />

          <p className="cm-footer">
            {lang === 'it'
              ? <>Sviluppato con ❤️ da Vector Minds<br />© 2025 Vector Minds — Tutti i diritti riservati</>
              : <>Built with ❤️ by Vector Minds<br />© 2025 Vector Minds — All rights reserved</>}
          </p>
        </div>
      </div>
    </div>
  );
}
